import type { NomadDB } from '~/db/NomadDB';
import { categoryHelper } from '~/lib/category/helper';

export const getActionType = (
  category: CategorySchema
): 'modal' | 'link' | 'video' => {
  const helper = categoryHelper();
  const { contentType } = category;
  switch (contentType) {
    case ContentTypes.Essential:
    case ContentTypes.Annals:
      return 'modal';
    case ContentTypes.Practice:
      if (
        helper.hasOnlyPosts(category) ||
        helper.hasOnlyOneCategory(category)
      ) {
        return 'modal';
      }
      return 'link';

    case ContentTypes.SubChapter:
    case ContentTypes.Chapter:
    case ContentTypes.Discipline:
      if (
        helper.hasOnlyPosts(category) &&
        helper.firstPostIs(category, PostTypes.Subject)
        /*
        (helper.firstPostIs(category, PostTypes.Subject) ||
          helper.hasOnlyPostsWithType(category, PostTypes.ContentPDF)) */ // remove  hasOnlyPostsWithType with contentPdf to fix list of contentPDF thumbnails
      ) {
        return 'modal';
      } else if (
        helper.hasOnlyAPost(category) &&
        !category.posts[0]?.content &&
        category.posts[0]?.medias?.length
      ) {
        if (category.posts[0].contentType === 'course_video') {
          return 'video';
        } else {
          return 'modal';
        }
      } else {
        return 'link';
      }
    default:
      return 'link';
  }
};

export const getModalSlug = (category: CategorySchema) => {
  const { contentType } = category;
  if (contentType === ContentTypes.Annals) {
    return category.slug;
  }
  if (category.children && category.children.length === 1) {
    return category.children[0].slug;
  }

  return category.slug;
};

export const getActionIconName = (category: CategorySchema) => {
  const helper = categoryHelper();
  const { contentType } = category;
  switch (contentType) {
    case ContentTypes.DisciplineSubSection:
    case ContentTypes.Chapter:
      return !helper.hasOnlyPosts(category) ? 'file' : 'course';
    case ContentTypes.Essential:
    case ContentTypes.Annals:
      return 'file';
    default:
      return 'course';
  }
};

export const getLinkedContent = (
  category: CategorySchema
): CategorySchema | PostSchema | ExerciseSchema => {
  let linkedContent: CategorySchema | PostSchema | ExerciseSchema;
  const helper = categoryHelper();
  const { contentType } = category;
  switch (contentType) {
    case ContentTypes.DisciplineSubSection:
    case ContentTypes.Chapter:
    case ContentTypes.Discipline:
    case ContentTypes.SubChapter:
    case ContentTypes.InterDisciplineInfoIntox:
    case ContentTypes.InterDisciplineDailyQuiz:
      if (helper.hasOnlyAPost(category)) {
        linkedContent = {
          ...category.posts[0],
        };
      } else if (helper.hasOnlyAnExercise(category)) {
        linkedContent = {
          ...category.exercises[0],
        };
      } else {
        linkedContent = category;
      }
      break;

    default:
      linkedContent = category;
  }
  return linkedContent;
};

export const getIsLockedContent = (
  category: CategorySchema,
  isTypePostLocked: boolean,
  isTypeQuizLocked: boolean,
  isASubscriber: boolean
) => {
  const helper = categoryHelper();
  const { contentType, lockState } = category;
  switch (contentType) {
    case ContentTypes.DisciplineSubSection:
    case ContentTypes.Chapter:
    case ContentTypes.Discipline:
    case ContentTypes.SubChapter:
    case ContentTypes.InterDisciplineInfoIntox:
    case ContentTypes.InterDisciplineDailyQuiz:
      if (helper.hasOnlyAPost(category)) {
        if (contentType === ContentTypes.Chapter) {
          if (lockState === 'default') {
            return isTypePostLocked;
          } else if (lockState === 'unlocked') {
            return false;
          } else {
            return !isASubscriber;
          }
        }
      } else if (helper.hasOnlyAnExercise(category)) {
        if (contentType === 'chapter') {
          if (lockState === 'default') {
            return isTypeQuizLocked;
          } else if (lockState === 'unlocked') {
            return false;
          } else {
            return !isASubscriber;
          }
        }
      }
      break;
  }
};

export const getIsDeepLink = (category) => {
  return (
    typeof category.deeplink !== 'undefined' && category.deeplink.length > 0
  );
};

export const getActionRoute = (
  action: 'modal' | 'link' | 'video',
  content: UserContent,
  baseCategroy: CategorySchema,
  contentCategory: CategorySchema,
  linkedContent: CategorySchema | PostSchema | QuizSchema | ExerciseSchema,
  defaultPath: string = null,
  ancestors: CategorySchema[] = []
): { path: string } => {
  if (action === 'modal' || action === 'video') {
    // const path = defaultPath;
    let hash;
    if (action === 'modal') {
      hash = `/${action}/${getModalSlug(contentCategory)}`;
    } else {
      hash = `/${action}/${linkedContent.slug}`;
    }

    if (!defaultPath) {
      const discipline = ancestors.find(
        (link) => link.contentType === ContentTypes.Discipline
      );
      if (discipline) {
        defaultPath = `/${content.baseSection}/${content.slug}/${discipline.slug}`;
      } else {
        defaultPath = `/${content.baseSection}/${content.slug}/`;
      }
    }
    const fullPath = `${defaultPath}#${hash}`;

    return {
      path: fullPath,
    };
  }

  let section = content.baseSection;
  // if no default path it's come from search and can not go to home content
  if (defaultPath && content.type === UserContentType.MainContent) {
    if (
      defaultPath === '/' ||
      defaultPath.indexOf(`/${Sections.InTheNews}`) === 0
    ) {
      section = Sections.InTheNews;
    }
    if (defaultPath.indexOf(`/${Sections.ChildForYou}`) === 0) {
      section = Sections.ChildForYou;
    }
  }

  switch (linkedContent.type) {
    case 'exercise':
      return {
        path: `/${section}/${content.slug}/${contentCategory.slug}/exercise/${linkedContent.slug}`,
      };
    case 'post':
      return {
        path: `/${section}/${content.slug}/${contentCategory.slug}/post/${linkedContent.slug}`,
      };
    default: {
      let categorySlug = linkedContent.slug;
      if (
        linkedContent === contentCategory &&
        contentCategory.contentType === ContentTypes.SubChapter
      ) {
        categorySlug = baseCategroy.slug;
      }
      return {
        path: `/${section}/${content.slug}/${categorySlug}`,
      };
    }
  }
};

export const getCategoryRouterAction = async (
  $db: NomadDB,
  category: CategorySchema,
  content: UserContent,
  getIsTypeLocked: (lockedContent: string, contentType?: string) => boolean,
  defaultPath: string = null,
  ancestors: CategorySchema[] = []
) => {
  // defaultPath is null if function is call from the search. It will uses the ancestors.
  // defaultPath as a value for normal navigation
  const helper = categoryHelper();

  const contentCategory = await helper.ensureHasNotOnlyACategory($db, category);

  const linkedContent = getLinkedContent(contentCategory);

  const isLocked = getIsTypeLocked(
    linkedContent.lockedContent || linkedContent.contentType,
    linkedContent?.contentType || ''
  );

  const iconName: 'file' | 'course' = getActionIconName(contentCategory);

  const action: 'modal' | 'link' | 'video' = getActionType(contentCategory);

  const type = linkedContent.type as 'post' | 'category' | 'exercise';

  const routeLink = getActionRoute(
    action,
    content,
    category,
    contentCategory,
    linkedContent,
    defaultPath,
    ancestors
  );

  return {
    linkedContent,
    iconName,
    routeLink,
    action,
    isLocked,
    type,
    category,
  };
};
